import React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { V2 } from "../../../Api";
import { formatDateLocal, formatName, formatPhone } from "../../../Utilities";
import ViewToolbar from "../../ViewToolbar";
import ToolbarMenuItem from "../../ToolbarMenuItem";
import { allergySeverityToText, maritalStatusToText, raceToString } from "../../../TextTables";

import "../../../patient.css";
import "../../../list.css";


interface IPatientOverviewInnerViewProps extends RouteComponentProps<any> {

}

interface IPatientOverviewInnerViewState {
    isLoading: boolean | string;
    patient: any;
    allergies: any[];
}

class PatientOverviewInnerView extends React.Component<IPatientOverviewInnerViewProps, IPatientOverviewInnerViewState> {
    constructor(props: Readonly<IPatientOverviewInnerViewProps>) {
        super(props);

        const state: IPatientOverviewInnerViewState = {
            isLoading: true,
            patient: {},
            allergies: []
        };

        this.state = state;
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="patient-page-body">
                <div className="spinner spinner-center lds-dual-ring" />
            </div>;
        }
        if (typeof this.state.isLoading === "string") {
            return <div className="patient-page-body">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        return (
            <div className="patient-page-body">
                <ViewToolbar>
                    <ToolbarMenuItem right="patient:edit">
                        <Link to={`/patient/${this.props.match.params.id}/edit`} className="icon-pencil" title="Update patient info" />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h2>Basic Info</h2>
                        </header>

                        <dl className="info-list">
                            <div className="info-list-row">
                                <div className="col-50">
                                    <dt>Name</dt>
                                    <dd>{formatName(this.state.patient.name)}</dd>
                                </div>
                                <div className="col-37-5">
                                    <dt>Date of Birth</dt>
                                    <dd>{formatDateLocal(this.state.patient.birthdate) || "\u2014"}</dd>
                                </div>
                                <div className="col-12-5">
                                    <dt>Sex</dt>
                                    <dd>{this.state.patient.sex}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Marital Status</dt>
                                    <dd>{maritalStatusToText(this.state.patient.maritalStatus)}</dd>
                                </div>
                                <div>
                                    <dt>Race</dt>
                                    <dd>{raceToString(this.state.patient.race)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Phone</dt>
                                    <dd>{this.state.patient.phoneNumber ? <a href={`tel:${this.state.patient.phoneNumber}`}>{formatPhone(this.state.patient.phoneNumber)}</a> : "\u2014"}</dd>
                                </div>
                                <div>
                                    <dt>Email Address</dt>
                                    <dd>{this.state.patient.emailAddress ? <a href={`mailto:${this.state.patient.emailAddress}`}>{this.state.patient.emailAddress}</a> : "\u2014"}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Address</dt>
                                    {(this.state.patient.address?.state || this.state.patient.address?.city)
                                        ? <>
                                            <dd>{this.state.patient.address.line1}</dd>
                                            {this.state.patient.address.line2 ? <dd>{this.state.patient.address.line2}</dd> : null}
                                            <dd>{this.state.patient.address.city}, {this.state.patient.address.state} {this.state.patient.address.zip}</dd>
                                        </>
                                        : <dd>{"\u2014"}</dd>}
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Insurance Company</dt>
                                    <dd>{this.state.patient.insuranceCompany || "\u2014"}</dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                    {this.state.allergies.length === 0
                        ? <section className="info-block">
                            <header>Allergies</header>
                            No allergies reported.
                        </section>
                        : <table className="section-table align-top table-vertical">
                            <thead>
                                <tr>
                                    <th colSpan={4}>Allergies</th>
                                </tr>
                                <tr>
                                    <th colSpan={2} className="col-smallest">Severity</th>
                                    <th>Allergen</th>
                                    <th>Reaction</th>
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Allergies</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.allergies.map(a => <tr key={a.id}>
                                    <td className="col-smallest inline" data-caption="Severity: ">{allergySeverityToText(a.severity)}</td>
                                    <td className="col-smallest">{this.allergySeverityToIcon(a.severity)}</td>
                                    <td>{a.value}</td>
                                    <td>{a.reaction}</td>
                                </tr>)}
                            </tbody>
                        </table>}
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const [result, allergyResult] = await Promise.all([
                V2.get<any>(`patients/${this.props.match.params.id}`),
                V2.get<any>(`patients/${this.props.match.params.id}/allergies`),
            ]);

            allergyResult.allergies.sort((a: any, b: any) => b.severity - a.severity);

            this.setState({
                isLoading: false,
                patient: result,
                allergies: allergyResult.allergies
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load data"
            });
        }
    }

    private allergySeverityToIcon(severity: number) {
        switch (severity) {
            case 0: return <i className="icon-question" />;
            case 1: return null;
            case 2: return <i className="icon-warning red" />;
            default: return null;
        }
    }
}

export default PatientOverviewInnerView;
